import { atomWithReset } from "jotai/utils"
import { AppLoadingState } from "@/store/undoable-state"
import { store } from "./store"
import { atom, Getter } from "jotai"
import { isCalibrationAtom, isCullingModeAtom, projectAtom } from "./project/projects-state"
import {
	expandedPanelsAtom,
	showingCropPanelAtom,
	showingHealingPanelAtom,
	showingMaskingPanelAtom,
} from "./ui-views-state"
import { healingModeAtom } from "./editor/healing-state"
import {
	allPhotosAtom,
	cullingInfoAtom,
	currentPhotoAtom,
	isExpressSetupAtom,
	metadataAtom,
	photosAtom,
} from "./photo/photos-state"
import { photosViewAtom } from "./photo/photos-view-state"

import { photoLoadingStateAtom } from "@/store/editor/photo-loading"
import { uuid } from "@/utils/uuid"
import { userQueryAtom } from "@/api/users-api"
import { OnboardingState, defaultOnboardingState } from "./onboarding/onboarding-state"
import { updateOnboardingState } from "./onboarding/onboarding-service"
import { activeAdjustmentsAtom, currentAdjustmentsAtom } from "./store-history"
import { projectSessionAtom } from "@/routing/project-session"

export interface AIModelLoadingState {
	model: string | null
	progress: number | null
}

export const AIModelLoadingStateAtom = atomWithReset<AIModelLoadingState>({
	model: null,
	progress: null,
})

export const loadingStateAtom = atomWithReset<AppLoadingState>({ status: null })

export const isImportingAtom = atom((get) => {
	return get(loadingStateAtom).status === "importing"
})

export const metadataCompletedCountAtom = atom<number>((get) => {
	const photos = get(allPhotosAtom)
	const allMetadata = get(metadataAtom)

	const completeMetadata = photos.filter((photo) => {
		const metadata = allMetadata[photo.id]
		return metadata && metadata.isComplete
	})
	return completeMetadata.length
})

export const metadataFailureCountAtom = atom(0)

// Live extraction progress is separate from committed metadata: culling must wait
// for the metadata buffer to be flushed before sorting photos by capture time.
export const metadataIndexingProgressAtom = atom<{
	projectId: string
	completed: number
	total: number
} | null>(null)

export const metadataFinishedCountAtom = atom((get) => {
	const metadataCompletedCount = get(metadataCompletedCountAtom)
	return metadataCompletedCount + get(metadataFailureCountAtom)
})

export const metadataProgressAtom = atom<number>((get) => {
	const photos = get(allPhotosAtom)
	return Math.floor((get(metadataFinishedCountAtom) / photos.length) * 100)
})

export const isMetadataFinishedAtom = atom((get) => {
	const count = get(metadataFinishedCountAtom)
	const photos = get(allPhotosAtom)

	return count >= photos.length
})

//TODO: need to handle when getting culling info failed cases
export const clusteringProgressAtom = atom<number>((get) => {
	const photos = get(photosAtom)
	const photoCount = Object.keys(photos).length
	const cullingInfo = get(cullingInfoAtom)
	const clusteringCompleteCount = Object.keys(cullingInfo).filter(
		(photoId) => photos[photoId],
	).length

	return Math.round((clusteringCompleteCount / photoCount) * 100)
})

export const scoringCompleteCountAtom = atom<number>((get) => {
	const photos = get(photosAtom)
	return Object.values(get(cullingInfoAtom)).filter(
		(val) => photos[val.photoId] && val.scoreItem !== undefined,
	).length
})

export const scoringProgressAtom = atom<number>((get) => {
	const photos = get(allPhotosAtom)
	return Math.round((get(scoringCompleteCountAtom) / photos.length) * 100)
})

export const criticalFailureAtom = atom<boolean>(false)

export const sessionIdAtom = atom<string>(uuid())

export const editingDisabledAtom = atom((get) => {
	const loadingState = get(photoLoadingStateAtom)
	const previewState = get(photosViewAtom)
	const currentPhoto = get(currentPhotoAtom)

	return (
		!currentPhoto ||
		currentPhoto.isUnsupported ||
		loadingState === "loading" ||
		previewState === "grid" ||
		currentPhoto?.status === "applying" ||
		currentPhoto?.status === "pending"
	)
})

// Helper atom to check if user onboarding data is loaded
export const isOnboardingLoadedAtom = atom((get) => {
	const { data: user } = get(userQueryAtom)
	return !!user
})

export const onboardingStateAtom = atom(
	(get) => {
		const { data: user } = get(userQueryAtom)

		if (user?.onboardingState) {
			// Convert backend format to frontend format
			return {
				welcomeShown: user.onboardingState.welcomeShown,
				welcomeCreditsShown: user.onboardingState.welcomeCreditsShown,
				intakeSurveyShown: user.onboardingState.intakeSurveyShown,
				unsubscribedSurveyShown: user.onboardingState.unsubscribedSurveyShown,
				createStyleShown: user.onboardingState.createStyleShown,
				calibrationTooltipsShown: user.onboardingState.calibrationTooltipsShown,
				improveStyleTooltipsShown: user.onboardingState.improveStyleTooltipsShown,
				editingTooltipsShown: user.onboardingState.editingTooltipsShown,
				expressSetupTooltipShown: user.onboardingState.expressSetupTooltipShown,
				presetAppliedShown: user.onboardingState.presetAppliedShown,
				presetAppliedToSuggestedShown: user.onboardingState.presetAppliedToSuggestedShown,
				colorMatchAppliedToSuggestedShown:
					user.onboardingState.colorMatchAppliedToSuggestedShown,
				fullCalibrationTooltipsShown: user.onboardingState.fullCalibrationTooltipsShown,
				markReferenceReminderTooltipShown:
					user.onboardingState.markReferenceReminderTooltipShown,
				continueReferenceReminderTooltipShown:
					user.onboardingState.continueReferenceReminderTooltipShown,
				adaptiveMaskTooltipShown: user.onboardingState.adaptiveMaskTooltipShown,
				adaptiveHealingTooltipShown: user.onboardingState.adaptiveHealingTooltipShown,
				adaptiveCropTooltipShown: user.onboardingState.adaptiveCropTooltipShown ?? 0,
				adaptivePerspectiveTooltipShown:
					user.onboardingState.adaptivePerspectiveTooltipShown ?? 0,
				cullingShown: user.onboardingState.cullingShown,
				cullingTooltipsShown: user.onboardingState.cullingTooltipsShown,
				manualCullingTooltipsShown: user.onboardingState.manualCullingTooltipsShown,
				hiddenPhotosTooltipShown: user.onboardingState.hiddenPhotosTooltipShown,
				sessionCount: user.onboardingState.sessionCount,
			}
		}
		return defaultOnboardingState
	},
	async (get, _set, updatedState: OnboardingState) => {
		// Only update backend if user is logged in
		const { data: user } = get(userQueryAtom)
		if (!user) {
			console.warn("Cannot update onboarding state: user not logged in")
			return
		}
		await updateOnboardingState(updatedState)
	},
)

export const sessionCountAtom = atom(
	(get) => get(onboardingStateAtom).sessionCount,
	async (get, set, update: number | ((prev: number) => number)) => {
		const current = get(onboardingStateAtom)
		const newCount = typeof update === "function" ? update(current.sessionCount) : update
		await set(onboardingStateAtom, { ...current, sessionCount: newCount })
	},
)

export const hasShownOnboarding = (key: keyof OnboardingState, index?: number) => {
	const current = store.get(onboardingStateAtom)
	switch (key) {
		case "welcomeShown":
		case "intakeSurveyShown":
		case "createStyleShown":
		case "presetAppliedShown":
		case "presetAppliedToSuggestedShown":
		case "colorMatchAppliedToSuggestedShown":
		case "cullingShown":
		case "hiddenPhotosTooltipShown":
		case "welcomeCreditsShown":
		case "unsubscribedSurveyShown":
			return current[key]
	}
	return index !== undefined && current[key] >= index
}

export const markOnboardingShown = (key: keyof OnboardingState, index?: number) => {
	const current = store.get(onboardingStateAtom)
	const updated = { ...current, [key]: index ?? true }
	store.set(onboardingStateAtom, updated)
}

export const resetOnboardingShown = (key: keyof OnboardingState) => {
	const current = store.get(onboardingStateAtom)
	const updated = { ...current, [key]: false }
	store.set(onboardingStateAtom, updated)
}

const isProjectSessionOpenAtom = atom((get) => {
	const project = get(projectAtom)
	if (!project) {
		return false
	}
	const session = get(projectSessionAtom)
	return session.status === "open" && session.projectId === project.id
})

export const showingExpressFilmstripTooltipAtom = atom<boolean>((get) => {
	return (
		get(isProjectSessionOpenAtom) &&
		get(isCalibrationAtom) &&
		get(isExpressSetupAtom) &&
		get(onboardingStateAtom).createStyleShown &&
		get(onboardingStateAtom).expressSetupTooltipShown === 0
	)
})

export const showingExpressMarkReferenceTooltipAtom = atom<boolean>((get) => {
	return (
		get(isProjectSessionOpenAtom) &&
		get(onboardingStateAtom).createStyleShown &&
		get(isCalibrationAtom) &&
		get(isExpressSetupAtom) &&
		get(onboardingStateAtom).expressSetupTooltipShown === 1
	)
})

export const showingAdvancedMarkReferenceTooltipAtom = atom<boolean>((get) => {
	return (
		get(isProjectSessionOpenAtom) &&
		get(onboardingStateAtom).createStyleShown &&
		get(isCalibrationAtom) &&
		!get(isExpressSetupAtom) &&
		get(onboardingStateAtom).calibrationTooltipsShown === 0
	)
})

export const showingAdvancedSuggestedTooltipAtom = atom<boolean>((get) => {
	return (
		get(isProjectSessionOpenAtom) &&
		get(isCalibrationAtom) &&
		!get(isExpressSetupAtom) &&
		get(onboardingStateAtom).createStyleShown &&
		get(onboardingStateAtom).calibrationTooltipsShown === 1
	)
})

export const showingImproveMarkReferenceTooltipAtom = atom<boolean>((get) => {
	return (
		get(isProjectSessionOpenAtom) &&
		get(onboardingStateAtom).improveStyleTooltipsShown === 0 &&
		!get(isCalibrationAtom) &&
		get(currentPhotoAtom)?.styleId != null &&
		get(photosViewAtom) != "grid"
	)
})

export const showingImproveLockTooltipAtom = atom<boolean>((get) => {
	return (
		get(isProjectSessionOpenAtom) &&
		get(onboardingStateAtom).improveStyleTooltipsShown === 1 &&
		!get(isCalibrationAtom) &&
		get(currentPhotoAtom)?.styleId != null &&
		get(photosViewAtom) != "grid"
	)
})

export const showingEditingModeTooltipAtom = atom<boolean>((get) => {
	return (
		get(isProjectSessionOpenAtom) &&
		get(onboardingStateAtom).editingTooltipsShown === 0 &&
		!get(isCalibrationAtom) &&
		get(currentPhotoAtom)?.styleId != null &&
		get(photosViewAtom) === "grid"
	)
})

export const shouldShowMarkReferenceReminderAtom = atom<boolean>(false)
export const showingMarkReferenceReminderAtom = atom<boolean>((get) => {
	return (
		get(shouldShowMarkReferenceReminderAtom) &&
		get(onboardingStateAtom).markReferenceReminderTooltipShown !== 1
	)
})

export const referenceMarkedSinceSetup = atom<boolean>(false)
export const lockedSinceSetup = atom<number>(0)

export const unsupportedPhotoTooltipAtom = atom<boolean>(false)
export const showUnsupportedTooltip = () => {
	store.set(unsupportedPhotoTooltipAtom, true)
}

export const showingAdaptiveMaskTooltipAtom = atom<boolean>((get) => {
	const hasMasks = (get(currentAdjustmentsAtom).localAdjustments?.length ?? 0) > 0
	return (
		get(showingMaskingPanelAtom) &&
		hasMasks &&
		get(currentPhotoAtom)?.kind !== "reference" &&
		get(onboardingStateAtom).adaptiveMaskTooltipShown !== 1
	)
})

export const showingAdaptiveHealingTooltipAtom = atom<boolean>((get) => {
	return (
		get(showingHealingPanelAtom) &&
		get(healingModeAtom) === "healing" &&
		get(currentPhotoAtom)?.kind !== "reference" &&
		get(onboardingStateAtom).adaptiveHealingTooltipShown !== 1
	)
})

export const showingAdaptiveCropTooltipAtom = atom<boolean>((get) => {
	return (
		get(showingCropPanelAtom) &&
		get(currentPhotoAtom)?.kind !== "reference" &&
		get(onboardingStateAtom).adaptiveCropTooltipShown !== 1
	)
})

export const showingAdaptivePerspectiveTooltipAtom = atom<boolean>((get) => {
	const transformExpanded = get(expandedPanelsAtom)["transform"] === true
	const hasPerspectiveChange = get(activeAdjustmentsAtom).perspectiveCorrection
	return (
		transformExpanded &&
		hasPerspectiveChange &&
		get(currentPhotoAtom)?.kind !== "reference" &&
		get(onboardingStateAtom).adaptivePerspectiveTooltipShown !== 1
	)
})

const shouldShowCullingTooltip = (
	get: Getter,
	autoIndex: number,
	manualIndex: number | null = null,
): boolean => {
	if (!get(isCullingModeAtom) || !get(isProjectSessionOpenAtom)) {
		return false
	}

	const onboarding = get(onboardingStateAtom)
	if (!onboarding.cullingShown) {
		return false
	}

	const isManual = get(isManualCullingAtom)

	if (isManual) {
		if (manualIndex === null) return false
		return onboarding.manualCullingTooltipsShown === manualIndex
	} else {
		return onboarding.cullingTooltipsShown === autoIndex
	}
}

export const showingCullingGroupTooltipAtom = atom(
	(get) => shouldShowCullingTooltip(get, 0, 0), // smart: 0, manual: 0
)

export const showingSmartCullingFiltersTooltipAtom = atom(
	(get) => shouldShowCullingTooltip(get, 1, null), // smart: 1, manual: null
)

export const showingCullingFilterTooltipAtom = atom(
	(get) => shouldShowCullingTooltip(get, 2, 1), // smart: 2, manual: 1
)

export const showingCullingEditTooltipAtom = atom(
	(get) => shouldShowCullingTooltip(get, 3, 2), // smart: 3, manual: 2
)

// Help to check if user is in manual culling mode (all smart culling features disabled)
export const isManualCullingAtom = atom<boolean>((get) => {
	const project = get(projectAtom)
	if (!project?.cullingSettings) return false

	const settings = project.cullingSettings
	return (
		!settings.suggestKeepers &&
		!settings.groupSimilarPhotos &&
		!settings.rejectBlurryPhotos &&
		!settings.rejectPoorFaceQuality &&
		!settings.rejectPoorExposure
	)
})

export const cullingHiddenPhotosTooltipAtom = atom<boolean>(false)
export const showCullingHiddenPhotosTooltip = () => {
	if (
		store.get(cullingHiddenPhotosTooltipAtom) ||
		store.get(onboardingStateAtom).hiddenPhotosTooltipShown
	) {
		return
	}
	store.set(cullingHiddenPhotosTooltipAtom, true)
}

export const showingWelcomeDialogAtom = atom((get) => {
	if (!get(isOnboardingLoadedAtom)) return false

	const onboardingState = get(onboardingStateAtom)
	return onboardingState.welcomeShown === false && onboardingState.intakeSurveyShown === false
})

export const showingSubscriptionDialogAtom = atom((get) => {
	if (get(showingWelcomeDialogAtom)) {
		return false
	}

	const isArrivingFromSubscriptionLink = window.location.hash === "#subscription_page"
	return isArrivingFromSubscriptionLink
})

// Disable showing intake survey for https://app.asana.com/1/1113185975437517/project/1204918935759297/task/1211665926169603
export const showingIntakeSurveyDialogAtom = atom(false)

// Don't show select project dialog automatically for https://app.asana.com/1/1113185975437517/project/1204918935759297/task/1211665926169603
export const showingSelectProjectDialogAtom = atom(false)
